import { Button } from '@tempo-io/sui-button';
import { Checkbox } from '@tempo-io/sui-checkbox';
import { cn } from '@tempo-io/sui-class-utils';
import { Close, LeftPanel } from '@tempo-io/sui-icons';
import { Input } from '@tempo-io/sui-input';
import { ScrollArea } from '@tempo-io/sui-scrollarea';
import { Separator } from '@tempo-io/sui-separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@tempo-io/sui-sheet';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useFlags } from '@/lib/hooks/feature-flags/useFlagsWithOverrides';
import { useFeatureFlagOverrides } from '@/lib/providers/featureFlagOverrideContext';
import type { FlagKeys } from '@/lib/providers/featureFlagProvider';

const SIDE_STORAGE_KEY = '__nexus_devtools_side__';

interface FeatureFlagDevToolsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const loadSide = (): 'left' | 'right' => {
  const stored = localStorage.getItem(SIDE_STORAGE_KEY);
  if (stored === 'left' || stored === 'right') {
    return stored;
  }
  return 'right';
};

const saveSide = (side: 'left' | 'right'): void => {
  localStorage.setItem(SIDE_STORAGE_KEY, side);
};

export const FeatureFlagDevToolsDialog: React.FC<FeatureFlagDevToolsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const ldFlags = useFlags<FlagKeys>();
  const { overrides, setFlagOverride, clearFlagOverride, clearAllOverrides } =
    useFeatureFlagOverrides();
  const [searchQuery, setSearchQuery] = useState('');
  const [flashingFlag, setFlashingFlag] = useState<string | null>(null);
  const [copiedFlag, setCopiedFlag] = useState<string | null>(null);
  const [side, setSide] = useState<'left' | 'right'>(loadSide);

  // Get all flag names sorted - overridden flags first, then alphabetically
  const { flagNames, overriddenFlags, nonOverriddenFlags } = useMemo(() => {
    const allFlags = Object.keys(ldFlags) as Array<keyof FlagKeys>;

    // Separate overridden and non-overridden flags
    const overriddenFlags = allFlags.filter((flag) => flag in overrides).sort();
    const nonOverriddenFlags = allFlags.filter((flag) => !(flag in overrides)).sort();

    // Overridden flags first, then the rest
    return {
      flagNames: [...overriddenFlags, ...nonOverriddenFlags],
      overriddenFlags,
      nonOverriddenFlags,
    };
  }, [ldFlags, overrides]);

  // Filter flags based on search query
  const filteredFlagNames = useMemo(() => {
    if (!searchQuery.trim()) {
      return flagNames;
    }
    const query = searchQuery.toLowerCase();
    return flagNames.filter((flagName) => String(flagName).toLowerCase().includes(query));
  }, [flagNames, searchQuery]);

  // Prepare filtered flag data for rendering
  const flagsToRender = useMemo(() => {
    return filteredFlagNames
      .map((flagName, index) => {
        const ldValue = ldFlags[flagName];
        const hasOverride = flagName in overrides;
        const currentValue = hasOverride ? overrides[flagName] : ldValue;

        // Skip non-boolean flags (like string flags)
        if (typeof ldValue !== 'boolean') {
          return null;
        }

        // Show separator when transitioning from overridden to non-overridden flags
        const showSeparator =
          !searchQuery.trim() &&
          index === overriddenFlags.length &&
          overriddenFlags.length > 0 &&
          nonOverriddenFlags.length > 0;

        return {
          flagName,
          ldValue,
          hasOverride,
          currentValue,
          showSeparator,
        };
      })
      .filter(Boolean);
  }, [
    filteredFlagNames,
    ldFlags,
    overrides,
    searchQuery,
    overriddenFlags.length,
    nonOverriddenFlags.length,
  ]);

  const handleToggle = (flagName: keyof FlagKeys, checked: boolean) => {
    const ldValue = ldFlags[flagName];

    if (checked === ldValue) {
      // If the new value matches LaunchDarkly's value, clear the override
      clearFlagOverride(flagName);
    } else {
      // Otherwise, set the override
      setFlagOverride(flagName, checked);
    }

    // Flash animation feedback
    setFlashingFlag(String(flagName));
    setTimeout(() => setFlashingFlag(null), 300);
  };

  const handleResetAll = () => {
    clearAllOverrides();
  };

  const handleCopyFlagName = async (flagName: string) => {
    await navigator.clipboard.writeText(flagName);
    setCopiedFlag(flagName);
    setTimeout(() => setCopiedFlag(null), 2000);
  };

  // Persist side preference to localStorage
  useEffect(() => {
    saveSide(side);
  }, [side]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side={side}
        className={cn(
          'w-[550px] sm:max-w-[550px] bg-slate-900 text-slate-100 flex flex-col p-0',
          side === 'left' ? 'border-r-2 border-amber-500' : 'border-l-2 border-amber-500',
        )}
      >
        <SheetHeader className="px-6 pt-6 pb-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-amber-500 text-slate-900 text-xs font-bold rounded">
              DEV TOOLS
            </span>
            <SheetTitle className="text-slate-100 font-mono">Feature Flag Overrides</SheetTitle>
          </div>
          <SheetDescription className="text-slate-400 font-mono text-sm">
            Override feature flags locally without changing LaunchDarkly. Changes persist in
            localStorage.
          </SheetDescription>
        </SheetHeader>

        <Separator className="bg-slate-700" />

        <div className="px-6 py-4">
          <div className="relative flex items-center">
            <Input
              type="text"
              placeholder="Search flags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-amber-500 pr-10 w-full"
            />
            {searchQuery && (
              <Button
                onClick={() => setSearchQuery('')}
                variant="ghost"
                size="icon"
                className="absolute right-2 h-8 w-8 text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                title="Clear search"
                type="button"
              >
                <Close className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden px-6">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-2">
              {overriddenFlags.length > 0 && !searchQuery.trim() && (
                <div className="flex items-center gap-2 py-2">
                  <Separator className="flex-1 bg-slate-600" />
                  <span className="text-xs text-slate-500 font-mono">Overridden flags</span>
                  <Separator className="flex-1 bg-slate-600" />
                </div>
              )}
              {flagsToRender.length === 0 ? (
                <div className="text-center py-8 text-slate-400 font-mono text-sm">
                  No flags found matching &quot;{searchQuery}&quot;
                </div>
              ) : null}
              {flagsToRender.map((flag) => {
                if (!flag) return null;
                const { flagName, ldValue, hasOverride, currentValue, showSeparator } = flag;

                return (
                  <Fragment key={String(flagName)}>
                    {showSeparator && (
                      <div className="flex items-center gap-2 py-2">
                        <Separator className="flex-1 bg-slate-600" />
                        <span className="text-xs text-slate-500 font-mono">Other flags</span>
                        <Separator className="flex-1 bg-slate-600" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'flex items-center justify-between p-3 rounded-md bg-slate-800 border border-slate-700 hover:border-slate-600 transition-all duration-300',
                        flashingFlag === String(flagName) && 'bg-amber-500/20 border-amber-500',
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Checkbox
                          id={String(flagName)}
                          checked={currentValue as boolean}
                          onCheckedChange={(checked) => handleToggle(flagName, checked as boolean)}
                          className="border-slate-500"
                        />
                        <label
                          htmlFor={String(flagName)}
                          className="flex-1 cursor-pointer select-none"
                        >
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="font-mono text-sm text-slate-100 cursor-pointer hover:text-amber-500 transition-colors bg-transparent border-none p-0"
                              onClick={(e) => {
                                e.preventDefault();
                                handleCopyFlagName(String(flagName));
                              }}
                              title="Click to copy flag name"
                            >
                              {String(flagName)}
                            </button>
                            {copiedFlag === String(flagName) && (
                              <span className="text-xs text-green-400 font-mono animate-in fade-in duration-200">
                                Copied!
                              </span>
                            )}
                            {hasOverride && (
                              <span className="px-1.5 py-0.5 bg-amber-500 text-slate-900 text-xs font-bold rounded">
                                OVERRIDE
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 font-mono mt-0.5">
                            LaunchDarkly: {ldValue ? 'true' : 'false'}
                          </div>
                        </label>
                      </div>
                      {hasOverride && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            clearFlagOverride(flagName);
                          }}
                          className="h-8 w-8 text-slate-400 hover:text-slate-100 hover:bg-slate-700 shrink-0"
                          title="Clear override"
                          type="button"
                        >
                          <Close className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <div className="px-6 py-4 border-t border-slate-700 mt-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-xs text-slate-500 font-mono">
                {Object.keys(overrides).length} override(s) active
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSide('left')}
                  className={cn(
                    'h-7 w-7',
                    side === 'left'
                      ? 'bg-amber-500 text-slate-900 hover:bg-amber-600 hover:text-slate-900'
                      : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800',
                  )}
                  title="Dock to left"
                  type="button"
                >
                  <LeftPanel className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSide('right')}
                  className={cn(
                    'h-7 w-7',
                    side === 'right'
                      ? 'bg-amber-500 text-slate-900 hover:bg-amber-600 hover:text-slate-900'
                      : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800',
                  )}
                  title="Dock to right"
                  type="button"
                >
                  <LeftPanel className="w-4 h-4 scale-x-[-1]" />
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleResetAll}
                disabled={Object.keys(overrides).length === 0}
                className="border-slate-600 text-slate-900 hover:bg-slate-800 hover:text-slate-100 disabled:opacity-50"
              >
                Reset All
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-amber-500 text-slate-900 hover:bg-amber-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
